import first from 'lodash-es/first';
import initial from 'lodash-es/initial';
import last from 'lodash-es/last';
import {
  ellipsisCharacter as ellipsis,
} from '../Utils';
import {
  IFontMeasurement,
  IFontSizeDetermination,
  ILayoutAttemptResult,
  ILine,
  IWord,
  wordSeparator,
} from './fitTextInRectangle';

export const availableSpaceAsMultipleOfRadius = 1.5;

interface ICircle {
  radius: number;
}

// Figure out how far away from the center of a circle a chord is:
const getDistanceFromCenterForChordLength =
  (radius: number, chordLength: number) => {
    return Math.sqrt( radius ** 2 - (chordLength / 2) ** 2);
  };

// Similar to above:
const getChordLengthAtDistanceFromCenter =
  (radius: number, distance: number) =>
    2 * Math.sqrt(radius ** 2 - distance ** 2);

// Return the the total length available for the `n`-th line in a circle,
// assuming the first line starts at the very top of the circle:
const getTotalLineLengthGetter =
  (radius: number, lineHeight: number, firstLineYTop: number) =>
    // `lineNumber` is zero-based:
    (lineNumber: number) => {

  // const distanceFromCenterTop = Math.abs(
  //   radius - lineNumber * lineHeight - firstLineYTop
  // );
  const distanceOfLineTop = Math.abs(firstLineYTop + lineNumber * lineHeight - radius);
  const chordLengthTop = getChordLengthAtDistanceFromCenter(radius, distanceOfLineTop);

  // const distanceFromCenterBottom = Math.abs(
  //   radius - (lineNumber + 1) * lineHeight - firstLineYTop
  // );
  const distanceOfLineBottom = Math.abs(firstLineYTop + (lineNumber + 1) * lineHeight - radius);
  const chordLengthBottom = getChordLengthAtDistanceFromCenter(radius, distanceOfLineBottom);

  return Math.min(chordLengthTop, chordLengthBottom);
};

export const attemptToFitTextInCircle = (input: {
    text: string,
    referenceMeasurement: IFontMeasurement,
    referenceFontSize: number,
    circle: ICircle,
    fontSizeToTry: number,
  }): ILayoutAttemptResult => {

  const {
    text, referenceMeasurement, referenceFontSize, circle, fontSizeToTry,
  } = input;

  // Scale up/down the measured width/height according to the `fontSizeToTry`:
  const maxCharacterHeight = fontSizeToTry / referenceFontSize * referenceMeasurement.maxCharacterHeight;
  const maxCharacterWidth = fontSizeToTry / referenceFontSize * referenceMeasurement.maxCharacterWidth;

  const {radius} = circle;
  const words: IWord[] = text.split(wordSeparator).map(word => ({
    text: word,
    width: word.length * maxCharacterWidth,
  }));
  const [firstWord, ...remainingWords] = words;

  const isEveryWordShorterThanDiameter = words.every(
    word => word.width < availableSpaceAsMultipleOfRadius * radius,
  );

  // If at least one word is longer than the diameter or twice the diameter is
  // shorter than the height of a single lint of text, the text is too big to
  // fit. (We need twice the diameter because if the diameter is equal to a line
  // of text's height then there's no room to actually put the first character because
  // the top of the circle correspond to the top of the first line):
  if (!isEveryWordShorterThanDiameter ||
        maxCharacterHeight > availableSpaceAsMultipleOfRadius * radius) {
    return {
      success: false,
      lines: [],
    };
  } else {

    const firstLineYTop = getDistanceFromCenterForChordLength(radius, firstWord.width);
    const maxLineNumber = Math.floor((2 * radius - firstLineYTop) / maxCharacterHeight);

    const getLineLength = getTotalLineLengthGetter(radius, maxCharacterHeight, firstLineYTop);

    let lineNumber = 0;
    const firstLineLength = getLineLength(lineNumber);
    let lineLengthLeft = firstLineLength - firstWord.width;
    const fitResult: IWord[][] = [ [firstWord] ];

    const convertFitResultToLines = (result: IWord[][]) => {
      // Filter out empty lines because they can be added if the layout process
      // terminates because it goes past the bottom of the circle:
      const resultWithoutEmptyLists = result.filter(list => list.length > 0);

      const convertedLines: ILine[] = resultWithoutEmptyLists.map(wordsInLine => {
        const wordsBeforeLast = initial(wordsInLine);
        const lastWord = last(wordsInLine)!;
        const newWordsBeforeLast = wordsBeforeLast.map(word => ({
          text: word.text + wordSeparator,
          width: word.width + maxCharacterWidth,
        }));
        const newWords = [...newWordsBeforeLast, lastWord];
        const newText = newWords.map(word => word.text).join('');
        return {
          text: newText,
          words: newWords,
        };
      });
      return convertedLines;
    };

    while (remainingWords.length) {
      const wordToFit = remainingWords.shift()!;

      if (wordToFit.width < lineLengthLeft) {
        // If there's enough space left on the current line for the word then add
        // it to the line:{
        const lastFitResult = last(fitResult)!;
        lastFitResult.push(wordToFit);

        // Need to also subtract the width by the amount taken by the space
        // separating the words:
        lineLengthLeft = lineLengthLeft - wordToFit.width - maxCharacterWidth;
      } else {
        // Else, start a new line if possible
        lineNumber = lineNumber + 1;
        // If we're going pass the bottom of the circle, stop the layout process
        // and report failure:
        if (lineNumber > maxLineNumber) {
          const linesFromWords = convertFitResultToLines(fitResult);
          return {
            success: false,
            lines: linesFromWords,
          };
        } else {
          fitResult.push([]);
          // Return the attempted word to the pool of remaining words to fit:
          remainingWords.unshift(wordToFit!);
          lineLengthLeft = getLineLength(lineNumber);
        }
      }
    }
    const lines = convertFitResultToLines(fitResult);
    const isSuccessful = (lines.length <= maxLineNumber);

    return {
      success: isSuccessful,
      lines,
    };

  }
};

export const determineFontSizeToFit = (
  input: {
    text: string,
    referenceMeasurement: IFontMeasurement,
    referenceFontSize: number,
    circle: ICircle,
  }): IFontSizeDetermination => {

  const {text, referenceFontSize, referenceMeasurement, circle} = input;
  const {maxCharacterHeight, maxCharacterWidth} = referenceMeasurement;
  const totalTextArea = text.length * maxCharacterHeight * maxCharacterWidth;
  const totalCircleArea = Math.PI * circle.radius ** 2;

  // This is the font size for which the total text area = rectangle's area.
  // This is the max font size the text can possibly have:
  const maxFontSize = (Math.sqrt((totalCircleArea / totalTextArea) * Math.pow(referenceFontSize, 2)));

  const attemptAtMaxFontSize = attemptToFitTextInCircle({
    text, referenceMeasurement, referenceFontSize, circle,
    fontSizeToTry: maxFontSize,
  });
  if (attemptAtMaxFontSize.success === true) {
    return {
      fontSize: maxFontSize,
      lines: attemptAtMaxFontSize.lines,
    };
  } else {
    // Use bisection to figure out the optimize font size. Stop when the two bounds

    // converge to within 0.01:
    let upperBound = maxFontSize;
    let lowerBound = 0;
    let sizeToTry: number | undefined;
    let layoutResult: ILine[] | undefined;
    while (Math.abs(upperBound - lowerBound) > 0.01) {
      sizeToTry = (upperBound + lowerBound) / 2;
      // TODO: memoize this function call:
      const layoutAttempt = attemptToFitTextInCircle({
        text, referenceMeasurement, referenceFontSize, circle,
        fontSizeToTry: sizeToTry,
      });
      let newUpperBound: number, newLowerBound: number;
      if (layoutAttempt.success) {
        layoutResult = layoutAttempt.lines;
        newUpperBound = upperBound;
        newLowerBound = sizeToTry;
      } else {
        newUpperBound = sizeToTry;
        newLowerBound = lowerBound;
      }
      upperBound = newUpperBound;
      lowerBound = newLowerBound;
    }

    return {
      fontSize: sizeToTry!,
      lines: layoutResult!,
    };

  }
};

export const truncateTextInCircle = (input: {
    text: string,
    referenceMeasurement: IFontMeasurement,
    referenceFontSize: number,
    circle: ICircle,
    fontSizeToTry: number;

  }): ILine[] => {

  const {
    fontSizeToTry, referenceFontSize, referenceMeasurement,
    circle: {radius},
  } = input;

  const maxCharacterHeight = fontSizeToTry / referenceFontSize * referenceMeasurement.maxCharacterHeight;
  const maxCharacterWidth = fontSizeToTry / referenceFontSize * referenceMeasurement.maxCharacterWidth;

  const wordsToFit = input.text.split(wordSeparator);
  const firstWord = first(wordsToFit)!;

  // Try taking away a word at a time until the text fits...
  while (wordsToFit.length > 0) {
    const fitResult = attemptToFitTextInCircle({
      ...input,
      text: wordsToFit.join(wordSeparator),
    });

    if (fitResult.success === true) {
      // Append
      const {lines} = fitResult;
      const linesBeforeLast = initial(lines);
      const lastLine = last(lines)!;
      const {words} = lastLine;
      const wordsBeforeLast = initial(words);
      const lastWord = last(words)!;
      const newLastWord = {
        text: lastWord.text.replace(/.$/, ellipsis),
        width: lastWord.width,
      };
      const newLastLine = {
        text: lastLine.text.replace(/.$/, ellipsis),
        words: [...wordsBeforeLast, newLastWord],
      };
      return [...linesBeforeLast, newLastLine];
    } else {
      wordsToFit.pop();
    }
  }

  // If after taking away the last word and the text still doesn't fit, try
  // truncating the first word until it fits:
  const maxLineLength = getChordLengthAtDistanceFromCenter(radius, maxCharacterHeight / 2);
  const maxCharactersCountToFitWidth = maxLineLength / maxCharacterWidth;
  const trimmedFirstWord = `${firstWord.slice(0, maxCharactersCountToFitWidth)}${ellipsis}`;
  const result: ILine[] = [{
    text: trimmedFirstWord,
    words: [{
      text: trimmedFirstWord,
      width: maxCharactersCountToFitWidth * maxCharacterWidth,
    }],
  }];
  return result;
};
